/**
 * Hold-window drain bar (spec §3, amended 2026-07-06).
 *
 * A thin bar pinned to the bottom edge of its parent card that empties as the
 * hold window runs out - it lets the remaining time be read at a glance,
 * without focusing on the numeric countdown. Rendered on both hold-window
 * surfaces (Tier-2 toast and the Approvals-page held card) so they stay
 * visually identical.
 *
 * The parent card must have `position: relative` (both cards already do).
 *
 * The component owns its own once-per-second clock, started outside the
 * Angular zone: signal updates still refresh the template, but the interval
 * never counts as pending work, so tests using `fixture.whenStable()` do not
 * hang while a held card is on screen. It also exposes `remainingSeconds()`
 * so the parent template can show the matching "Sends in Ns" text from the
 * same clock (via a template reference variable).
 */

import { ChangeDetectionStrategy, Component, computed, inject, input, NgZone, OnDestroy, signal } from '@angular/core';

import { holdRemainingSeconds, holdTotalSeconds } from './hold-window-time';

@Component({
    selector: 'app-hold-drain-bar',
    template: `<div class="hold-drain-bar__fill" data-testid="hold-drain-bar-fill" [style.width.%]="fillPercent()"></div>`,
    styles: `
        :host {
            position: absolute;
            right: 0;
            bottom: 0;
            left: 0;
            display: block;
            height: 3px;
            overflow: hidden;
            border-radius: 0 0 0.375rem 0.375rem;
            background: #fef3c7;
        }

        .hold-drain-bar__fill {
            height: 100%;
            background: #d97706;
            transition: width 1s linear;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HoldDrainBar implements OnDestroy {
    /** Server-authoritative moment the held send fires (naive UTC). */
    readonly fireAt = input<string | null>(null);

    /** When the hold was queued - defines the bar's 100% mark. */
    readonly createdAt = input<string | null>(null);

    private readonly zone = inject(NgZone);

    /** Once-per-second counter; each bump recomputes the countdown. */
    private readonly tick = signal(0);
    private readonly intervalId = this.zone.runOutsideAngular(() => setInterval(() => this.tick.update(n => n + 1), 1000));

    /** Whole seconds left on the hold - shared with the parent's text label. */
    readonly remainingSeconds = computed(() => {
        this.tick();
        return holdRemainingSeconds(this.fireAt());
    });

    /** Bar fill as a percentage of the full hold window. */
    readonly fillPercent = computed(() => {
        const total = holdTotalSeconds(this.createdAt(), this.fireAt());
        return Math.min(100, Math.max(0, (this.remainingSeconds() / total) * 100));
    });

    ngOnDestroy(): void {
        clearInterval(this.intervalId);
    }
}
